/**
 * The rules that already block save today, ported into the registry.
 *
 * These carry no new behaviour — each one reproduces a gate that currently
 * lives as an ad-hoc early return in `BuilderHeader.formSaveAndUpdate` or as a
 * flag feeding `isSaveDisabled`. Porting them first lets the registry be proven
 * against the existing gates before it takes over the save path.
 *
 * They correspond to PRD Error 6: "surface any other condition that currently
 * causes save to fail".
 */
import { isDateTimeElementValid, isDateTimeField } from "@/util/dateTimeSerialize";
import { isPictureChoiceField, validatePictureChoiceElement } from "@/util/pictureChoice";
import { hasIncompleteUrlValidation } from "@/util/urlField";
import type { BuilderContext, RuleResult, ValidationRule } from "../types";

const ALL_ASSETS: ValidationRule["assetTypes"] = ["form", "survey", "quiz"];

/** Shorthand for the common "one finding per offending element" shape. */
const resultFor = (
  ruleId: string,
  section: ValidationRule["section"],
  entry: BuilderContext["elements"][number],
  params?: Record<string, string | number>,
  sidebarTab?: string
): RuleResult => ({
  ruleId,
  severity: "error",
  section,
  key: `${ruleId}:${entry.elementKey}`,
  params,
  target: {
    elementIndex: entry.elementIndex,
    slideIndex: entry.slideIndex,
    elementKey: entry.elementKey,
    sidebarTab,
  },
});

/** Date & Time controls only render inside the CfV1 rollout. */
const dateTimeGate = (ctx: BuilderContext): boolean => ctx.dateTimeRolloutEnabled;

export const urlFieldDomainsMissing: ValidationRule = {
  id: "url_field_domains_missing",
  severity: "error",
  assetTypes: ALL_ASSETS,
  section: "fields",
  evaluate: (ctx) =>
    ctx.elements
      .filter((entry) => hasIncompleteUrlValidation([entry.element]))
      .map((entry) => resultFor("url_field_domains_missing", "fields", entry)),
};

/**
 * Picture Choice produces four distinct rules from one validator call. The
 * existing gate reported only the first offending element and stopped; here
 * every element is swept so the popover can list them all.
 */
const PICTURE_CHOICE_RULES: Array<{ id: string; codes: string[] }> = [
  { id: "picture_choice_image_missing", codes: ["MISSING_OPTION_IMAGE"] },
  { id: "picture_choice_label_missing", codes: ["MISSING_OPTION_LABEL"] },
  { id: "picture_choice_duplicate_labels", codes: ["DUPLICATE_OPTION_LABEL"] },
  {
    id: "picture_choice_selection_limit_invalid",
    codes: ["INVALID_EXACT_LIMIT", "INVALID_RANGE_LIMIT"],
  },
];

const pictureChoiceRules: ValidationRule[] = PICTURE_CHOICE_RULES.map(
  ({ id, codes }) => ({
    id,
    severity: "error" as const,
    assetTypes: ALL_ASSETS,
    section: "fields" as const,
    evaluate: (ctx: BuilderContext): RuleResult[] =>
      ctx.elements
        .filter((entry) => isPictureChoiceField(entry.element))
        .filter((entry) =>
          validatePictureChoiceElement(entry.element).errors.some((error) =>
            codes.includes(error.code)
          )
        )
        // Picture Choice options — their images, labels and selection limit —
        // are all edited under the sidebar's Options tab, not Content.
        .map((entry) => resultFor(id, "fields", entry, undefined, "Options")),
  })
);

/**
 * One gate in the builder, five distinct user-facing problems. `isDateTimeElementValid`
 * returns a single boolean, so each rule re-derives its own condition rather than
 * reading a reason code that does not exist.
 */
const dateTimeRules: ValidationRule[] = [
  {
    id: "datetime_label_empty",
    predicate: (element: Record<string, unknown>) =>
      !String(element.label ?? "").trim(),
  },
  {
    id: "datetime_time_format_invalid",
    predicate: (element: Record<string, unknown>) =>
      element.defaultTime === "custom" && !isDateTimeElementValid(element as Parameters<typeof isDateTimeElementValid>[0]),
  },
  {
    id: "datetime_no_available_days",
    predicate: (element: Record<string, unknown>) => {
      const mode = element.dateMode ?? "any";
      if (mode !== "future_only" && mode !== "range") return false;
      const days = (element.days ?? {}) as Record<string, boolean>;
      return !["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].some(
        (day) => days[day] === true
      );
    },
  },
  {
    id: "datetime_date_range_invalid",
    predicate: (element: Record<string, unknown>) => {
      if (element.dateMode !== "range") return false;
      const min = element.dateRangeMin as string | undefined;
      const max = element.dateRangeMax as string | undefined;
      return !min || !max || max < min;
    },
  },
  {
    id: "datetime_time_range_invalid",
    predicate: (element: Record<string, unknown>) => {
      if (element.timeMode !== "range") return false;
      const start = element.timeRangeStart as string | undefined;
      const end = element.timeRangeEnd as string | undefined;
      if (!start || !end) return true;
      return !isDateTimeElementValid(element as Parameters<typeof isDateTimeElementValid>[0]);
    },
  },
].map(({ id, predicate }) => ({
  id,
  severity: "error" as const,
  assetTypes: ALL_ASSETS,
  section: "fields" as const,
  gate: dateTimeGate,
  evaluate: (ctx: BuilderContext): RuleResult[] =>
    ctx.elements
      .filter((entry) => isDateTimeField(entry.element))
      .filter((entry) => predicate(entry.element as unknown as Record<string, unknown>))
      .map((entry) => resultFor(id, "fields", entry)),
}));

export const opportunitySettingsIncomplete: ValidationRule = {
  id: "opportunity_settings_incomplete",
  severity: "error",
  assetTypes: ALL_ASSETS,
  section: "settings",
  evaluate: (ctx) => {
    if (!ctx.hasOpportunityField || ctx.pipelineStageValid) return [];

    // Opportunity settings open from the field's own sidebar, not the builder's
    // Settings tab, so the finding points at the field and asks for the modal.
    const field = ctx.elements.find(
      (entry) =>
        (entry.element as { model?: string }).model === "opportunity"
    );

    return [
      {
        ruleId: "opportunity_settings_incomplete",
        severity: "error",
        section: "settings",
        key: "opportunity_settings_incomplete",
        target: {
          elementIndex: field?.elementIndex,
          slideIndex: field?.slideIndex,
          elementKey: field?.elementKey,
          settingsTab: "opportunities",
        },
      },
    ];
  },
};

/**
 * Footer styles disable save through a store flag rather than a validator, and
 * with no message at all today. The rule turns that dead button into something
 * the user can read.
 */
export const footerStylesInvalid: ValidationRule = {
  id: "footer_styles_invalid",
  severity: "error",
  assetTypes: ALL_ASSETS,
  section: "settings",
  evaluate: (ctx) =>
    ctx.saveEnabledByOtherSurfaces
      ? []
      : [
          {
            ruleId: "footer_styles_invalid",
            severity: "error",
            section: "settings",
            key: "footer_styles_invalid",
            target: { settingsTab: "styles" },
          },
        ],
};

export const existingBlockingRules: ValidationRule[] = [
  urlFieldDomainsMissing,
  ...pictureChoiceRules,
  ...dateTimeRules,
  opportunitySettingsIncomplete,
  footerStylesInvalid,
];
